import { api } from '../components/role-play/apiService';
import { UserRole } from '../types';

/**
 * Demo role proxy — lets a super admin "view as" a lower role so every role can be
 * demonstrated from one account.
 *
 * The proxy lives server-side (keyed by the caller's Keycloak ID), so once it is set
 * every request behaves as the demonstrated role without the frontend attaching
 * anything per call. That also means switching role requires re-reading auth state:
 * callers should follow a start/stop with `refreshAuth()`, which AuthContext's
 * `startDemoProxy` / `stopDemoProxy` already do.
 */

/** Roles a super admin can proxy into. Deliberately excludes super_admin. */
export const PROXYABLE_ROLES: UserRole[] = ['admin', 'trainer', 'trainee'];

export interface DemoProxyState {
  active: boolean;
  real_role: UserRole;
  role?: UserRole | null;
  organization_id?: string | null;
  organization_name?: string | null;
  expires_in_seconds?: number | null;
}

/** Read the caller's current proxy. Super-admin only (403 otherwise). */
export async function fetchDemoProxy(): Promise<DemoProxyState> {
  return api.get<DemoProxyState>('/user/me/proxy');
}

/** Start (or re-point) the proxy at `role` within `organizationId`. */
export async function startDemoProxy(role: UserRole, organizationId: string): Promise<DemoProxyState> {
  return api.put<DemoProxyState>('/user/me/proxy', { role, organization_id: organizationId });
}

/** End the proxy, restoring the caller's super-admin view. */
export async function stopDemoProxy(): Promise<DemoProxyState> {
  return api.delete<DemoProxyState>('/user/me/proxy');
}

export interface ProxyOrganizationOption {
  id: string;
  name: string;
}

/**
 * Organizations the proxy can point at.
 *
 * Uses the dedicated real-role-guarded endpoint rather than `GET /organization`,
 * which is org-scoped once a proxy is active and would only return the org already
 * being demoed — leaving no way to switch mid-demo.
 */
export async function fetchProxyOrganizations(search?: string): Promise<ProxyOrganizationOption[]> {
  const query = search ? `?search=${encodeURIComponent(search)}` : '';
  return api.get<ProxyOrganizationOption[]>(`/user/me/proxy/organizations${query}`);
}
